import React, { useCallback, useMemo, useState } from 'react';
import { compose } from 'redux';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { View, RefreshControl } from 'react-native';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import { useMutation } from '@apollo/client';

import { DetailContainer } from '../../../containers';
import { useRefreshPage } from '../../../hooks/useRefreshPage';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Spinner } from '../../../components';
import PageHeading from '../../../design/components/PageHeading';
import SetupSection from '../components/SetupSection';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupPhotoSection from '../components/SetupPhotoSection';
import SetupMoveStatus from '../components/SetupMoveStatus';
import { useOfflineMutation } from '../../../offline';
import { CREATE_MEDIA, MODIFY_MEDIA } from '../../../../common/graphql/media';
import { MODIFY_COMM_DEVICE } from '../../../../common/graphql/commDevice';
import { flattenObjectDataInArray } from '../../../../common/utils/general/flattenObjectData';
import { getUserName } from '../../../../common/utils/user/getUserName';
import { getStatusLabel } from '../../../utils/statusLabel';
import { DEFAULT_TICKET_DESCRIPTIONS } from './MarketSetupDetailHelpers';
import MarketSetupInformationSection from './MarketSetupInformationSection';
import MarketSetupKioskSection from './MarketSetupKioskSection';
import MarketSetupMarketSection from './MarketSetupMarketSection';
import { createMarketSetupDetailStyles } from './MarketSetupDetail.styles';

const MarketSetupDetail = ({
	data: marketSetup,
	modifyItem,
	refetch,
	isAssignedMarketSetupWorker,
}) => {
	const styles = useThemedStyles(createMarketSetupDetailStyles);
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const [ticketDescriptions, setTicketDescriptions] = useState(DEFAULT_TICKET_DESCRIPTIONS);
	const [createMedia] = useOfflineMutation(CREATE_MEDIA, { configKey: 'createMedia' });
	const [modifyMedia] = useMutation(MODIFY_MEDIA);
	const [modifyCommDevice] = useMutation(MODIFY_COMM_DEVICE);
	const safeMarketSetup = useMemo(() => marketSetup || {}, [marketSetup]);
	const assignedMarket = safeMarketSetup.machine || safeMarketSetup.plannedMachine;
	const hasAssignedMarket = Boolean(safeMarketSetup.machine?.id);
	const dueDateLabel = safeMarketSetup.locationSetup?.dueDate ? moment(safeMarketSetup.locationSetup.dueDate).format('DD MMM YYYY') : 'No due date';
	const statusLabel = getStatusLabel(safeMarketSetup.status, 'Unknown status');
	const headingSubText = `${dueDateLabel} · ${statusLabel}`;
	const locationSetupLabel = safeMarketSetup.locationSetup?.identifier || 'Unknown location setup';

	const updateMarketSetup = useCallback((values) => {
		return modifyItem(values);
	}, [modifyItem]);

	const addPhoto = useCallback(async ({ link, targetField }) => {
		const result = await createMedia({
			variables: {
				input: {
					machine: safeMarketSetup.machine?.id,
					marketSetup: safeMarketSetup.id,
					link,
				},
			},
		});

		const newMedia = result?.data?.createMedia;
		if (!newMedia?.id) {
			Toast.show('Unable to upload photo');
			return;
		}

		const existingIds = flattenObjectDataInArray(safeMarketSetup[targetField] || []);
		await updateMarketSetup({ [targetField]: [...existingIds, newMedia.id] });

		if (result?.offline) {
			Toast.show('Photo queued; will sync when online');
		}
		else {
			Toast.show('Photo uploaded');
		}
	}, [createMedia, safeMarketSetup, updateMarketSetup]);

	const handleUpdateMediaComment = useCallback(async (media, comment) => {
		try {
			await modifyMedia({
				variables: {
					input: {
						id: media.id,
						comment: comment || null,
					},
				},
			});
			Toast.show('Note saved');
		}
		catch (error) {
			Toast.show('Unable to save note');
		}
	}, [modifyMedia]);

	const handlePaymentDeviceSerialUpdate = useCallback(async (device, value) => {
		if (!device?.id) {
			return;
		}

		if (!isAssignedMarketSetupWorker) {
			try {
				await modifyCommDevice({
					variables: {
						input: {
							id: device.id,
							setupInformation: {
								...(device.setupInformation || {}),
								paymentDeviceSerialNo: value,
							},
						},
					},
				});
			}
			catch (error) {
				Toast.show('Unable to update device serial');
			}
		}

		await updateMarketSetup({
			paymentDevicesSerialNo: {
				...(safeMarketSetup.paymentDevicesSerialNo || {}),
				[device.id]: value,
			},
		});
	}, [isAssignedMarketSetupWorker, safeMarketSetup.paymentDevicesSerialNo, modifyCommDevice, updateMarketSetup]);

	const updateTicketDescription = useCallback((key, value) => {
		setTicketDescriptions(prev => ({
			...prev,
			[key]: value,
		}));
	}, []);

	// Mirrors the web detail view by sending the correct create*Ticket payload when the tech taps Generate.
	const handleGenerateTicket = useCallback(async (config) => {
		if (!hasAssignedMarket) {
			Toast.show('Select a market before generating tickets');
			return;
		}

		try {
			await updateMarketSetup({
				[config.requestField]: { description: ticketDescriptions[config.key] },
			});
			Toast.show('Ticket created');
		}
		catch (error) {
			Toast.show('Unable to create ticket');
		}
	}, [hasAssignedMarket, ticketDescriptions, updateMarketSetup]);

	if (!marketSetup) {
		return (
			<View style={styles.loaderContainer}>
				<Spinner />
			</View>
		);
	}

	return (
		<KeyboardAwareScrollView
			style={styles.scrollView}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			<View style={styles.content}>
				<PageHeading title={marketSetup.identifier} subtitle={headingSubText} />
				<SetupMoveStatus pointOfSaleLabel="Market" setup={safeMarketSetup} />

				{!isAssignedMarketSetupWorker ? (
					<MarketSetupInformationSection
						marketSetup={marketSetup}
						assignedMarket={assignedMarket}
						locationSetupLabel={locationSetupLabel}
						dueDateLabel={dueDateLabel}
						updateMarketSetup={updateMarketSetup}
						addPhoto={addPhoto}
						handleUpdateMediaComment={handleUpdateMediaComment}
					/>
				) : (
					<SetupSection title="Setup details">
						<SetupInfoRow label="Identifier" value={marketSetup.identifier} />
						<SetupInfoRow label="Location setup" value={locationSetupLabel} />
						<SetupInfoRow label="Due date" value={dueDateLabel} />
						<SetupInfoRow label="Location" value={marketSetup.locationSetup?.location?.name || 'Not selected'} />
						<SetupInfoRow label="Place" value={marketSetup.place?.name || 'Not selected'} />
						<SetupInfoRow label="Market" value={assignedMarket?.assetId || 'Not selected'} />
						<SetupInfoRow label="Route" value={marketSetup.route?.name || 'Not selected'} />
						<SetupInfoRow label="Planogram" value={marketSetup.planogram?.name || 'Not selected'} />
						<SetupInfoRow
							label="Kiosk(s)"
							value={marketSetup.commDevices?.map(device => device.deviceId).filter(Boolean).join(', ') || 'Not selected'}
						/>
						<SetupInfoRow
							label="Assignees"
							value={marketSetup.assignees?.map(assignee => getUserName(assignee)).filter(Boolean).join(', ') || 'None'}
						/>
						<SetupInfoRow label="Additional instructions" value={marketSetup.additionalInstructions || 'None'} />
						<SetupPhotoSection
							title="Site photos"
							photos={marketSetup.placePhotos || []}
							onAddPhoto={(link) => addPhoto({ link, targetField: 'placePhotos' })}
							onUpdateComment={handleUpdateMediaComment}
							addButtonLabel="Add site photo"
						/>
					</SetupSection>
				)}

				<MarketSetupKioskSection
					marketSetup={marketSetup}
					isAssignedMarketSetupWorker={isAssignedMarketSetupWorker}
					updateMarketSetup={updateMarketSetup}
					handlePaymentDeviceSerialUpdate={handlePaymentDeviceSerialUpdate}
				/>

				<MarketSetupMarketSection
					marketSetup={marketSetup}
					isAssignedMarketSetupWorker={isAssignedMarketSetupWorker}
					hasAssignedMarket={hasAssignedMarket}
					ticketDescriptions={ticketDescriptions}
					updateTicketDescription={updateTicketDescription}
					handleGenerateTicket={handleGenerateTicket}
					updateMarketSetup={updateMarketSetup}
					addPhoto={addPhoto}
					handleUpdateMediaComment={handleUpdateMediaComment}
				/>
			</View>
		</KeyboardAwareScrollView>
	);
};

export default compose(DetailContainer)(MarketSetupDetail);
